import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import { AgGridTable } from "components/tables/AgGridTable";
import Search from "examples/Search";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {  deleteProductBatch,  fetchProductBatches} from "../../../redux/slices/products";
import { sortAndFilter } from "components/tables/AgGridTable";
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
import {  closeCustomModalDialog,  openCustomModalDialog,  setModalLoader,} from "../../../redux/slices/modalSlice";
import StatusText from "common/component/StatusText";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useDebounce } from "hooks/useDebounce";
import { formatTimestampToWithoutTime } from "utilities/common";
import CircularProgressWithLabel from "common/CircularProgressWithLabel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddBatch from "../AddBatch";

export default function BatchTable({
  canCreate, canEdit, canDelete,
  formRender = { formRender },
  productId = "",
  batchHandler = () => { },
  editBatchHandler = () => { },
  onBatchSuccess
}) {
  const dispatch = useDispatch();
  const { isFetching = false, productBatches = {} } = useSelector((state) => state.products);
  const { user } = useSelector((state) => state.auth);
  const [isExpanded, setIsExpanded] = useState(false);
  const pageRef = useRef(0);
  const searchTermRef = useRef("");
  const mainGridApiRef = useRef("");
  const gridRefApi = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [batchReset, setBatchReset] = useState(true);
  const [linkState, setLinkState] = useState("")

  batchHandler = () => {
    setBatchReset(!batchReset);
  };

  useEffect(() => {
    setTimeout(() => {
      setLinkState("")
    }, 1000);
  }, [linkState])


  useEffect(() => {
    if (!isExpanded) return;
    dispatch(
      fetchProductBatches({
        filters: {
          skip: pageRef.current,
          limit: 100,
          userType: "admin",
          searchTerm: searchTermRef.current,
        },
        productId,
      })
    );
  }, [batchReset]);

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const onEditClicked = (props) => {
    batchHandler();
    editBatchHandler(props.data);
  };

  const onAddClicked = () => {
    batchHandler();
    editBatchHandler({});
  };

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "Purchase Date",
      field: "purchaseDate",
      key: "fullName",
      suppressMenu: false,
      cellRenderer: (props) => {
        if (props.value !== undefined) {
          return props.value;
        } else {
          return <img src="https://www.ag-grid.com/example-assets/loading.gif" />;
        }
      },
      minWidth: 120,
    },
    {
      headerName: "Batch ID",
      field: "productBatchId",
      suppressMenu: false,
      wrapText: true,
      autoHeight: true,
      minWidth: 200,
      cellRenderer: (props) => {
        const [tooltipMessage, setTooltipMessage] = useState("Click to copy");
        return (
          <>
            {/* Tooltip with dynamic message */}
            <Tooltip title={tooltipMessage} arrow>
              <span
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => {
                  const link = props?.data?.productBatchId;
                  navigator.clipboard.writeText(link)
                    .then(() => {
                      setTooltipMessage("Copied!"); // Change tooltip message after successful copy
                      setTimeout(() => setTooltipMessage("Click to copy"), 2000); // Reset after 2 seconds
                    })
                    .catch((err) => { });
                }}
              >
                {props?.data?.productBatchId}
              </span>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerName: "Unique ID",
      field: "uniqueNumber",
      suppressMenu: false,
      minWidth: 100,
    },
    {
      headerName: "Expiration Date",
      field: "expirationDate",
      suppressMenu: false,
      minWidth: 120,
    },
    {
      headerName: "Status",
      field: "status",
      suppressMenu: false,
      cellRenderer: (props) => {
        return <StatusText status={props?.data?.status} />;
      },
      minWidth: 100,
    },
    {
      headerName: "Purchase Quantity",
      field: "purchaseQuantity",
      suppressMenu: false,
      cellRenderer: (props) => {
        if (!props?.value) return "-";
        if (props.value?.toString()) {
          return `${props.value?.toString()}`;
        }
      },
      minWidth: 140,
    },
    {
      headerName: "Current Quantity",
      field: "currentQuantity",
      suppressMenu: false,
      cellRenderer: (props) => {
        if (!props?.value) return "-";
        if (props.value?.toString()) {
          return `${props.value?.toString()}`;
        }
      },
      minWidth: 140,
    },
    {
      headerName: "Sold Percentage",
      field: "soldPercentage",
      suppressMenu: false,
      cellRenderer: (props) => {
        if (props.value?.toString()) {
          return (
            <CircularProgressWithLabel
              variant="determinate"
              value={props?.value?.toString() || "0"}
            />
          );
        }
        return "-";
      },
      minWidth: 140,
      cellStyle: { display: "inline-flex" },
    },
    {
      headerName: "Unit Cost",
      field: "unitCost",
      suppressMenu: false,
      headerClass: "left-aligned-cell-header",
      cellRenderer: (props) => {
        if (!props?.value) return "-";
        if (props.value) {
          return (
            <Typography variant="body2" fontWeight="medium" lineHeight={1}>
              ${props.value}
            </Typography>
          );
        }
      },
      minWidth: 100,
    },
    {
      headerName: "Actions",
      cellRenderer: (props) => {
        return (
          <Box display="inline-flex" alignItems="center">
            <IconButton
            disabled={!canEdit}
              onClick={(e) => {
                e.stopPropagation();
                onEditClicked(props);
              }}
              color="primary"
              size="small"
              variant="primary"
            >
              <EditIcon width="16px" height="16px" />
            </IconButton>
            <IconButton
             disabled={!canDelete}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(
                  openCustomModalDialog({
                    modalData: { deleteHandler, deleteId: props.data?.productBatchId, deleteType: "batch" },
                    modal: "DELETE_MODAL",
                  })
                );
              }}
              color="error"
              size="small"
              variant="primary"
              sx={{ marginLeft: 1 }}
            >
              <DeleteIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: "right", justifyContent: "end" },
      headerClass: "right-aligned-cell-header",
      minWidth: 120,
      maxWidth: 100,
      pinned: "right",
    },
  ]);

  const deleteHandler = (productBatchId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteProductBatch({ productId: productId, productBatchId: productBatchId, updatedBy: user?.name }))
      .unwrap()
      .then(() => {
        dispatch(closeCustomModalDialog());
        pageRef.current = 0;
        getRows(gridRefApi.current);
      })
      .catch(() => {
        dispatch(setModalLoader(false));
      });
    if (onBatchSuccess) onBatchSuccess();
  };

  const handleAccordianToggle = (event, expanded) => {
    setIsExpanded(!isExpanded);
  };


  const getRows = async (params) => {
    gridRefApi.current = params;
    dispatch(
      fetchProductBatches({
        filters: {
          skip: pageRef.current,
          limit: 100,
          userType: "admin",
          searchTerm: searchTermRef.current,
        },
        productId,
      })
    )
      .unwrap()
      .then((res) => {
        const { productBatchDetails = {}, total = 0 } = res;
        const batchesArray = productBatchDetails?.batchData?.map((item) => {
          return {
            ...item,
            expirationDate: item?.expirationDate
              ? formatTimestampToWithoutTime(item?.expirationDate)
              : "-",
            purchaseDate: item?.purchaseDate
              ? formatTimestampToWithoutTime(item?.purchaseDate)
              : "-",
            id: item._id,
          };
        });
        const dataAfterSortingAndFiltering = sortAndFilter(
          batchesArray,
          params.sortModel,
          params.filterModel
        );
        params.successCallback(dataAfterSortingAndFiltering, total);
        // pageRef.current = pageRef.current + 20;
      })
      .catch(() => { });
  };

  const searchFn = () => {
    const dataSource = {
      getRows,
    };
    mainGridApiRef?.current?.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption("datasource", dataSource);
  };

  const onRowClicked = (row) => {
    batchHandler();
    editBatchHandler(row.data);
  };

  const searchHandler = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
    searchTermRef.current = value;
    handleSearch(value);
  };


  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (isFetching) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (productBatches?.total === 0 || !productBatches?.total) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [productBatches, isFetching]);


  return (<>
    {Object.keys(formRender).length > 0 && !batchReset ? (
      <AddBatch batchHandler={batchHandler} data={formRender} onSuccess={onBatchSuccess} />
    ) : !batchReset ? (
      <AddBatch batchHandler={batchHandler} data={formRender} isEmptyData={true} onSuccess={onBatchSuccess} />
    ) : (
      <>
        <Accordion expanded={isExpanded}
          onChange={handleAccordianToggle}
        >

          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h4' fontWeight="semibold" style={{ color: "#2E2E3A" }}>Batches</Typography>
          </AccordionSummary>
          <AccordionDetails className="bg-grey section-width-custom radius-0 py3 ">
            <Grid container alignItems="center" mb={4}>

              <Grid item xs={12} sm={12} className="d-flex flex-end gap-2  section-custom-search-field">
                <Search placeholder="Search" className="custom-search-field-custom" label="" value={searchTerm} onChange={searchHandler} />
                {
                  canCreate &&
                  <MDButton startIcon={<AddRoundedIcon fontSize="large" />}
                    variant="contained"
                    color="primary"
                    onClick={onAddClicked} >
                    Add Batch
                  </MDButton>
                }
              </Grid>
            </Grid>

            <div className="batch-table">
              {isExpanded &&
                <AgGridTable
                  col={columnDefs}
                  getRows={getRows}
                  pageRef={pageRef}
                  isLoading={isFetching}
                  mainGridApiRef={mainGridApiRef}
                  gridOptions={{
                    rowHeight: 74,
                  }}
                />}
            </div>

          </AccordionDetails>
        </Accordion>
      </>
    )}
  </>);
}
